/**
 * NX WebSocket Service
 * Handles WebSocket communication with NX. Connection uses fixed-port polling
 * (11189, then +1000 up to 3 times); the last successful port is remembered in
 * localStorage and tried first on subsequent connects.
 * Based on reference implementation from nx.js and ws_connect_server.js
 */

import { buildNXWebSocketUrl, getNXPortCandidates, NX_WS_LAST_PORT_KEY } from '../types/nx';

export interface NXExecutionResult {
    /** true when NX status === 0 */
    success: boolean;
    /** NX status code: 0 = success, 1 = failure/error */
    status?: 0 | 1;
    message: string;
    data?: any;
}

export type NXConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface NXWebSocketCallbacks {
    onConnectionChange?: (status: NXConnectionStatus) => void;
    onExecutionResult?: (result: NXExecutionResult) => void;
    onError?: (error: string) => void;
}

const NX_STATUS_FIELD = /"status"\s*:\s*(\d+)/;
const NX_MESSAGE_FIELD = /"message"\s*:\s*/;
const NX_NATIVE_SUCCESS_MARKER = /(?:\bsuccessfully\s+[a-z][a-z-]*\b|\b(?:operation|boolean|subtract)\s+completed\s+successfully\b|\bSuccess\s*:\s*)/i;
const NX_FAILURE_MARKER = /\b(?:error|exception|failed|failure|invalid|unable|rejected|not found)\b/i;

/**
 * NX CUSTOMER_AUTOMATION sends listing-window HTML, not raw stdout:
 * spaces become `&nbsp;` / `&#160;`, and long WriteLine output is wrapped with
 * `<br>` in the middle of tokens (paths, timestamps, JSON). Callers should not
 * have to undo that on every executeCode result.
 */
export function sanitizeNxExecutionText(text: string): string {
    let out = String(text ?? '')
        .replace(/&nbsp;|&#160;|&#x0*a0;/gi, ' ')
        .replace(/\u00a0/g, ' ');

    // Unwrap ListingWindow line-wraps first so JSON/paths stay intact, then
    // treat remaining <br> tags as real line breaks.
    let previous = '';
    while (previous !== out) {
        previous = out;
        out = out.replace(/(\S)<br\s*\/?\s*>(\S)/gi, '$1$2');
    }
    out = out.replace(/<br\s*\/?\s*>/gi, '\n');

    return out
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&amp;/gi, '&');
}

const hasNativeSuccessMarker = (message: string): boolean => (
    NX_NATIVE_SUCCESS_MARKER.test(message) && !NX_FAILURE_MARKER.test(message)
);

/**
 * Local copy of envelope unwrap — do not import nxCodeUtils from this file.
 * A static import there creates a webpack cycle with RagLinkAction.
 */
function unwrapNxExecutionEnvelope(raw: unknown): { status?: 0 | 1; message: string } | null {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) return null;

    try {
        let parsed: unknown = JSON.parse(trimmed);
        if (typeof parsed === 'string') {
            try {
                parsed = JSON.parse(parsed);
            } catch {
                // keep the inner string
            }
        }
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const record = parsed as Record<string, unknown>;
            if ('status' in record || 'message' in record) {
                const statusNum = Number(record.status);
                return {
                    status: Number.isFinite(statusNum) ? (statusNum === 0 ? 0 : 1) : undefined,
                    message: record.message == null ? '' : String(record.message)
                };
            }
        }
    } catch {
        // Invalid JSON — fall through to structural extract
    }

    if (!trimmed.startsWith('{')) return null;
    const messageMatch = trimmed.match(NX_MESSAGE_FIELD);
    if (!messageMatch || messageMatch.index === undefined) return null;
    const statusMatch = trimmed.match(NX_STATUS_FIELD);
    let body = trimmed.slice(messageMatch.index + messageMatch[0].length);
    body = body.replace(/\s*\}\s*$/, '');
    if (body.startsWith('"')) {
        body = body.slice(1);
        if (body.endsWith('"')) body = body.slice(0, -1);
    }
    return {
        status: statusMatch ? (Number(statusMatch[1]) === 0 ? 0 : 1) : undefined,
        message: body
    };
}

/**
 * Parse NX JA execution response.
 * New protocol: `{ "status": 0|1, "message": "..." }` where 0 = success, 1 = failure.
 * Falls back to legacy plain-text detection for older NX builds.
 */
export function parseNXExecutionResponse(response: unknown): NXExecutionResult {
    const raw = typeof response === 'string' ? response : String(response ?? '');
    const envelope = unwrapNxExecutionEnvelope(raw);
    const message = sanitizeNxExecutionText(envelope?.message ?? raw);

    if (envelope?.status !== undefined) {
        return {
            success: envelope.status === 0,
            status: envelope.status,
            message,
            data: message
        };
    }

    // Legacy plain-text fallback
    const nativeSuccess = hasNativeSuccessMarker(message);
    const success = message.includes('Execution was successful') || nativeSuccess;
    return {
        success,
        message,
        data: message
    };
}

export class NXWebSocketService {
    private socket: WebSocket | null = null;
    private connectPromise: Promise<boolean> | null = null;
    private connectionGeneration = 0;
    private cancelConnectAttempt: (() => void) | null = null;
    private connectionStatus: NXConnectionStatus = 'disconnected';
    private callbacks: NXWebSocketCallbacks = {};
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5; // Matches DEFAULT_NX_CONFIG
    private reconnectDelay = 2000; // 2 seconds
    private reconnectTimer: NodeJS.Timeout | null = null;
    /**
     * NX journals are one-at-a-time. Chain executeCode so silent skill scans
     * (listInstalled / catalog) cannot steal the in-flight Python result by
     * overwriting socket.onmessage.
     */
    private executeChain: Promise<void> = Promise.resolve();
    private pendingExecution: {
        resolve: (result: NXExecutionResult) => void;
        timer: ReturnType<typeof setTimeout>;
    } | null = null;

    /**
     * Initialize the service with callbacks.
     * If the socket is already open (e.g. component remounted after navigation),
     * the new component will immediately receive the current connection status so
     * its UI reflects reality without waiting for the next status-change event.
     */
    initialize(callbacks: NXWebSocketCallbacks) {
        this.callbacks = callbacks;

        // Sync current status to the newly registered callbacks so a remounted
        // component (e.g. switching back from ReuseLibrary to RagChat) knows the
        // connection is still alive and does not show a stale "disconnected" state.
        if (this.connectionStatus !== 'disconnected') {
            callbacks.onConnectionChange?.(this.connectionStatus);
        }
    }

    /**
     * Connect to NX WebSocket server.
     * Polls a fixed list of candidate ports (11189, then +1000 up to 3 times: 12189/13189/14189).
     * The first port that connects wins; if all fail the connection is reported as failed.
     */
    async connect(): Promise<boolean> {
        if (this.connectPromise) return this.connectPromise;
        if (this.isConnected()) return true;
        this.clearReconnectTimer();
        const generation = this.connectionGeneration;
        const attempt = this.connectPorts(generation);
        this.connectPromise = attempt;
        try {
            return await attempt;
        } finally {
            if (this.connectPromise === attempt) this.connectPromise = null;
        }
    }

    private async connectPorts(generation: number): Promise<boolean> {
        console.log('[NX WebSocket] 🔄 Connect() called');
        console.log('[NX WebSocket] Current socket state:', this.socket?.readyState);
        console.log('[NX WebSocket] Current connection status:', this.connectionStatus);

        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            console.log('[NX WebSocket] ✅ Already connected');
            return true;
        }

        this.setConnectionStatus('connecting');

        // Try the last successful port first (if any), then the fixed candidates as fallbacks.
        const candidatePorts = getNXPortCandidates(this.readLastPort());
        console.log('[NX WebSocket] 🔎 Candidate ports to poll:', candidatePorts.join(', '));

        for (const port of candidatePorts) {
            if (generation !== this.connectionGeneration) return false;
            console.log(`[NX WebSocket] 🔗 Trying port ${port}...`);
            const connected = await this.tryConnect(port);
            if (generation !== this.connectionGeneration) return false;
            if (connected) {
                console.log(`[NX WebSocket] ✅ Connected on port ${port}`);
                void this.ensureMcpRoutingConnection();
                this.saveLastPort(port); // Remember for next time so reconnects skip dead ports
                this.reconnectAttempts = 0; // Reset reconnection attempts on successful connection
                this.clearReconnectTimer();
                this.setConnectionStatus('connected');
                return true;
            }
            console.log(`[NX WebSocket] ❌ Port ${port} failed, trying next candidate...`);
        }

        console.log('[NX WebSocket] ❌ All candidate ports failed');
        this.setConnectionStatus('error');
        this.callbacks.onError?.(
            `WebSocket connection to NX failed on all ports (${candidatePorts.join(', ')}). Make sure NX is running with the WebSocket server enabled.`
        );
        return false;
    }

    private async ensureMcpRoutingConnection(): Promise<void> {
        try {
            // NX automation uses a direct browser-to-NX socket. MCP routing uses the
            // authenticated Socket.IO bridge, so keep that bridge connected as well.
            const { webSocketService } = await import('./websocket.service');
            const routed = await webSocketService.connect();
            if (!routed) {
                console.warn('[NX WebSocket] MCP routing bridge is unavailable');
            }
        } catch (error) {
            console.warn('[NX WebSocket] Failed to connect MCP routing bridge:', error);
        }
    }

    /**
     * Attempt a single WebSocket connection to the given port.
     * Resolves true on open, false on error/close/timeout. Never rejects.
     */
    private tryConnect(port: number): Promise<boolean> {
        return new Promise((resolve) => {
            let settled = false;
            let socket: WebSocket | null = null;
            let timer: ReturnType<typeof setTimeout> | undefined;
            const finish = (result: boolean) => {
                if (settled) return;
                settled = true;
                if (timer !== undefined) clearTimeout(timer);
                if (this.cancelConnectAttempt === cancel) this.cancelConnectAttempt = null;
                if (!result && socket) {
                    if (this.socket === socket) this.socket = null;
                    socket.onopen = socket.onclose = socket.onerror = socket.onmessage = null;
                    socket.close();
                }
                resolve(result);
            };
            const cancel = () => finish(false);
            this.cancelConnectAttempt = cancel;

            try {
                const wsUrl = buildNXWebSocketUrl(port);
                const activeSocket = new WebSocket(wsUrl);
                socket = activeSocket;
                this.socket = activeSocket;

                activeSocket.onopen = (event) => {
                    if (settled || this.socket !== activeSocket) return;
                    console.log(`[NX WebSocket] ✅ Connection opened on port ${port}`, event);
                    activeSocket.onmessage = (msgEvent) => {
                        if (this.socket !== activeSocket) return;
                        console.log('[NX WebSocket] 📨 Message received:', msgEvent.data);
                        this.handleMessage(msgEvent.data);
                    };
                    activeSocket.onclose = (closeEvent) => {
                        if (this.socket !== activeSocket) return;
                        this.releaseSocket();
                        console.log('[NX WebSocket] ❌ Connection closed', closeEvent.code, closeEvent.reason);
                        this.failPendingExecution('Error: NX WebSocket closed during execution');
                        this.setConnectionStatus('disconnected');
                        // A peer's normal close is not a manual user disconnect.
                        if (this.reconnectAttempts < this.maxReconnectAttempts) {
                            this.scheduleReconnect();
                        }
                    };
                    activeSocket.onerror = (errEvent) => {
                        if (this.socket !== activeSocket) return;
                        console.error('[NX WebSocket] ⚠️ Runtime connection error', errEvent);
                        this.releaseSocket();
                        this.failPendingExecution('Error: NX connection lost; execution outcome is unknown. Check NX before retrying.');
                        this.setConnectionStatus('disconnected');
                        if (this.reconnectAttempts < this.maxReconnectAttempts) this.scheduleReconnect();
                    };
                    finish(true);
                };

                activeSocket.onerror = (error) => {
                    console.log(`[NX WebSocket] ⚠️ Error connecting on port ${port}`, error);
                    finish(false);
                };

                activeSocket.onclose = () => {
                    // Closed before opening → this port is not available
                    finish(false);
                };

                // Per-port connection timeout. Kept short so polling remaining ports stays responsive.
                timer = setTimeout(() => {
                    if (!settled) {
                        console.log(`[NX WebSocket] ⏰ Connection timeout on port ${port}`);
                        finish(false);
                    }
                }, 5000);
            } catch (error) {
                console.error(`[NX WebSocket] 💥 Exception connecting on port ${port}:`, error);
                finish(false);
            }
        });
    }

    /**
     * Disconnect from NX WebSocket server
     */
    disconnect() {
        console.log('[NX WebSocket] Disconnecting...');
        this.connectionGeneration++;
        this.cancelConnectAttempt?.();
        this.connectPromise = null;
        this.clearReconnectTimer();
        this.reconnectAttempts = this.maxReconnectAttempts; // Prevent automatic reconnection
        this.failPendingExecution('Error: NX WebSocket disconnected');

        this.releaseSocket();

        this.setConnectionStatus('disconnected');
    }

    /**
     * Execute Python code in NX. Calls are serialized: the next journal is not
     * sent until the current one returns (or times out).
     */
    async executeCode(code: string, timeoutMs = 1800000): Promise<NXExecutionResult> {
        const generation = this.connectionGeneration;
        const run = () => generation === this.connectionGeneration
            ? this.executeCodeNow(code, timeoutMs)
            : Promise.resolve({ success: false, message: 'Error: NX execution cancelled by disconnect' });
        const result = this.executeChain.then(run, run);
        this.executeChain = result.then(() => undefined, () => undefined);
        return result;
    }

    private nxConnectionErrorResult(): NXExecutionResult {
        const message = '❌**NX Connection Error**:\n Failed to connect to NX. Please make sure NX is running with the WebSocket server enabled. AIEE automatically polls ports 11189, 12189, 13189 and 14189 — if none respond, verify NX is running and that its WebSocket port falls within this range (see NX → File → Help → Log File, search for **NX_CUSTOMER_AUTOMATION_PORT**).';
        return { success: false, message, data: message };
    }

    private failPendingExecution(message: string): void {
        this.settlePendingExecution({ success: false, message, data: message });
    }

    private settlePendingExecution(result: NXExecutionResult): boolean {
        const pending = this.pendingExecution;
        if (!pending) return false;
        this.pendingExecution = null;
        clearTimeout(pending.timer);
        // CUSTOMER_AUTOMATION is one message per socket. Retire it before the next
        // queued command, including on timeout, so a late result cannot satisfy another job.
        this.releaseSocket();
        this.setConnectionStatus('disconnected');
        pending.resolve(result);
        return true;
    }

    private releaseSocket(): void {
        const socket = this.socket;
        this.socket = null;
        if (!socket) return;
        socket.onopen = socket.onclose = socket.onerror = socket.onmessage = null;
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
            socket.close(1000, 'NX connection retired');
        }
    }

    private async executeCodeNow(code: string, timeoutMs: number): Promise<NXExecutionResult> {
        if (!this.isConnected()) {
            const connected = await this.connect();
            if (!connected) {
                return this.nxConnectionErrorResult();
            }
        }

        return new Promise((resolve) => {
            if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
                resolve({
                    success: false,
                    message: 'Error: WebSocket connection not available',
                    data: 'Error: WebSocket connection not available'
                });
                return;
            }

            const timer = setTimeout(() => {
                this.failPendingExecution('Error: Execution timeout - no response from NX. Outcome unknown; check NX before retrying.');
            }, timeoutMs); // Keep the 30-minute default for complex NX operations.

            this.pendingExecution = { resolve, timer };

            try {
                console.log('[NX WebSocket] Executing code:', code.substring(0, 100) + '...');
                this.socket.send(code);
            } catch (error) {
                console.error('[NX WebSocket] Error executing code:', error);
                this.failPendingExecution(`Execution error: ${error}`);
            }
        });
    }

    /**
     * Check if connected to NX
     */
    isConnected(): boolean {
        return this.socket?.readyState === WebSocket.OPEN;
    }

    /**
     * Get current connection status
     */
    getConnectionStatus(): NXConnectionStatus {
        return this.connectionStatus;
    }

    /**
     * Manual connection test - can be called from browser console
     */
    async testConnection(): Promise<void> {
        console.log('[NX WebSocket] 🧪 Manual connection test started');
        console.log('[NX WebSocket] Current status:', this.connectionStatus);
        console.log('[NX WebSocket] Socket exists:', !!this.socket);
        console.log('[NX WebSocket] Socket ready state:', this.socket?.readyState);

        const result = await this.connect();
        console.log('[NX WebSocket] Test connection result:', result);

        if (result) {
            console.log('[NX WebSocket] ✅ Test connection successful');
        } else {
            console.log('[NX WebSocket] ❌ Test connection failed');
        }
    }

    /**
     * Get detailed connection info for debugging
     */
    getDebugInfo(): any {
        return {
            connectionStatus: this.connectionStatus,
            socketExists: !!this.socket,
            socketReadyState: this.socket?.readyState,
            socketUrl: this.socket?.url,
            hasCallbacks: {
                onConnectionChange: !!this.callbacks.onConnectionChange,
                onExecutionResult: !!this.callbacks.onExecutionResult,
                onError: !!this.callbacks.onError
            }
        };
    }

    /**
     * Handle incoming messages from NX
     */
    private handleMessage(data: string) {
        console.log('[NX WebSocket] Received message:', data);
        const result = parseNXExecutionResponse(data);
        if (!this.settlePendingExecution(result)) {
            this.callbacks.onExecutionResult?.(result);
        }
    }

    /**
     * Set connection status and notify callbacks
     */
    private setConnectionStatus(status: NXConnectionStatus) {
        const previousStatus = this.connectionStatus;
        if (this.connectionStatus !== status) {
            this.connectionStatus = status;
            console.log(`[NX WebSocket] 🔄 Status changed: ${previousStatus} → ${status}`);
            console.log(`[NX WebSocket] Notifying callbacks of status change`);
            this.callbacks.onConnectionChange?.(status);
        } else {
            console.log(`[NX WebSocket] Status unchanged: ${status}`);
        }
    }

    /**
     * Schedule a reconnection attempt
     */
    private scheduleReconnect() {
        if (this.reconnectTimer) {
            return; // Already scheduled
        }

        this.reconnectAttempts++;
        const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

        console.log(`[NX WebSocket] 🔄 Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

        this.reconnectTimer = setTimeout(async () => {
            this.reconnectTimer = null;
            const generation = this.connectionGeneration;
            console.log(`[NX WebSocket] 🔄 Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

            const connected = await this.connect();
            if (generation !== this.connectionGeneration) return;
            if (!connected && this.reconnectAttempts < this.maxReconnectAttempts) {
                this.scheduleReconnect();
            } else if (!connected) {
                console.log('[NX WebSocket] ❌ Max reconnection attempts reached');
                this.callbacks.onError?.('Failed to reconnect to NX WebSocket after maximum attempts');
            }
        }, delay);
    }

    /**
     * Clear the reconnection timer
     */
    private clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    /**
     * Read the last successfully connected port from localStorage.
     * Returns undefined if none stored or the value is invalid.
     */
    private readLastPort(): number | undefined {
        try {
            const raw = localStorage.getItem(NX_WS_LAST_PORT_KEY);
            const port = raw ? Number(raw) : NaN;
            return Number.isInteger(port) && port > 0 ? port : undefined;
        } catch {
            return undefined; // localStorage unavailable (e.g. privacy mode)
        }
    }

    /**
     * Persist the successfully connected port so the next connect tries it first.
     */
    private saveLastPort(port: number): void {
        try {
            localStorage.setItem(NX_WS_LAST_PORT_KEY, String(port));
        } catch {
            // Ignore write failures (e.g. storage full / disabled)
        }
    }
}

// Export singleton instance
export const nxWebSocketService = new NXWebSocketService();

// Expose debug functions to window object for console testing
if (typeof window !== 'undefined') {
    (window as any).nxDebug = {
        testConnection: () => nxWebSocketService.testConnection(),
        getDebugInfo: () => nxWebSocketService.getDebugInfo(),
        connect: () => nxWebSocketService.connect(),
        disconnect: () => nxWebSocketService.disconnect(),
        getStatus: () => nxWebSocketService.getConnectionStatus(),
        isConnected: () => nxWebSocketService.isConnected()
    };
    console.log('[NX WebSocket] 🔧 Debug functions exposed to window.nxDebug');
}

export default nxWebSocketService;
